import { useEffect, useRef, useState } from "react";
import { addDays, todayIn, zonedParts, type IsoDate } from "../../../shared/dates";
import type { CalendarEvent, CalendarInfo, EventInput, Member } from "../../../shared/models";
import { useEventActions } from "../../data/queries";
import { Avatar } from "../../ui/Avatar";
import { Confirm, Sheet } from "../../ui/Sheet";

const hhmm = (minutes: number) => `${String(Math.floor(minutes / 60) % 24).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
const toMinutes = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

interface Draft {
  title: string;
  calendarId: string;
  allDay: boolean;
  startDate: IsoDate;
  endDate: IsoDate; // all-day: last day (inclusive); timed: end date
  startTime: string;
  endTime: string;
  location: string;
  description: string;
  memberIds: string[];
}

function draftFor(event: CalendarEvent | null, day: IsoDate, calendars: CalendarInfo[], timeZone: string): Draft {
  if (event) {
    const base = { title: event.title, calendarId: event.calendarId, location: event.location ?? "", description: event.description ?? "", memberIds: event.people };
    if (event.allDay) {
      const last = addDays(event.end.date ?? addDays(event.start.date!, 1), -1);
      return { ...base, allDay: true, startDate: event.start.date!, endDate: last < event.start.date! ? event.start.date! : last, startTime: "09:00", endTime: "10:00" };
    }
    const s = zonedParts(new Date(event.start.dateTime!), timeZone);
    const e = zonedParts(new Date(event.end.dateTime ?? event.start.dateTime!), timeZone);
    return { ...base, allDay: false, startDate: s.date, endDate: e.date, startTime: hhmm(s.minutes), endTime: hhmm(e.minutes) };
  }
  // New events start at the next whole hour today, otherwise 9 AM.
  const now = zonedParts(new Date(), timeZone);
  const start = day === todayIn(timeZone) ? Math.min(23 * 60, Math.ceil((now.minutes + 1) / 60) * 60) : 9 * 60;
  const target = calendars.find((c) => c.isDefault && c.canEdit) ?? calendars.find((c) => c.canEdit);
  return {
    title: "",
    calendarId: target?.id ?? "",
    allDay: false,
    startDate: day,
    endDate: day,
    startTime: hhmm(start),
    endTime: hhmm(Math.min(start + 60, 23 * 60 + 59)),
    location: "",
    description: "",
    memberIds: [],
  };
}

/** Add or edit an event. Nothing is shown as saved until Google confirms it. */
export function EventForm({
  event,
  day,
  calendars,
  members,
  timeZone,
  onClose,
}: {
  event: CalendarEvent | null;
  day: IsoDate;
  calendars: CalendarInfo[];
  members: Member[];
  timeZone: string;
  onClose: () => void;
}) {
  const actions = useEventActions();
  const [d, setD] = useState<Draft>(() => draftFor(event, day, calendars, timeZone));
  const [scope, setScope] = useState<"one" | "series">("one");
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [error]);
  const writable = calendars.filter((c) => c.canEdit);
  const set = (patch: Partial<Draft>) => {
    setError(null);
    setD((x) => ({ ...x, ...patch }));
  };
  const pending = actions.create.isPending || actions.update.isPending;

  const setStartDate = (startDate: IsoDate) => set({ startDate, endDate: d.endDate < startDate || d.endDate === d.startDate ? startDate : d.endDate });
  const setStartTime = (startTime: string) => {
    // Keep the event's length when the start moves.
    const length = toMinutes(d.endTime) - toMinutes(d.startTime);
    const end = toMinutes(startTime) + (d.startDate === d.endDate && length > 0 ? length : 60);
    set({ startTime, endTime: end < 24 * 60 ? hhmm(end) : d.endTime });
  };

  const save = () => {
    if (!d.title.trim()) return setError("Give the event a title.");
    const times: EventInput = d.allDay
      ? { allDay: true, startDate: d.startDate, endDate: addDays(d.endDate < d.startDate ? d.startDate : d.endDate, 1) }
      : { allDay: false, startLocal: `${d.startDate}T${d.startTime}`, endLocal: `${d.endDate}T${d.endTime}` };
    if (!d.allDay && times.endLocal! <= times.startLocal!) return setError("The event must end after it starts.");
    const done = { onSuccess: onClose, onError: (e: Error) => setError(e.message) };
    const fields = { title: d.title.trim(), location: d.location.trim(), description: d.description.trim(), ...times };
    if (event) actions.update.mutate({ event, input: { ...fields, scope: event.recurringEventId ? scope : "one" } }, done);
    else actions.create.mutate({ ...fields, calendarId: d.calendarId, memberIds: d.memberIds }, done);
  };

  return (
    <Sheet
      title={event ? "Edit event" : "New event"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={pending || (!event && !d.calendarId)}>
            {pending ? "Saving to Google…" : "Save"}
          </button>
        </>
      }
    >
      <div className="form event-form">
        <label className="field">
          <span>Title</span>
          <input className="input input-lg" value={d.title} maxLength={200} autoFocus={!event} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. Dentist appointment" />
        </label>
        {!event && writable.length > 1 && (
          <label className="field">
            <span>Calendar</span>
            <select className="input" value={d.calendarId} onChange={(e) => set({ calendarId: e.target.value })}>
              {writable.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field field-toggle">
          <span>All day</span>
          <input type="checkbox" className="switch" checked={d.allDay} onChange={(e) => set({ allDay: e.target.checked })} />
        </label>
        {d.allDay ? (
          <div className="row event-form-times">
            <label className="field">
              <span>First day</span>
              <input type="date" className="input" value={d.startDate} onChange={(e) => e.target.value && setStartDate(e.target.value)} />
            </label>
            <label className="field">
              <span>Last day</span>
              <input type="date" className="input" value={d.endDate} min={d.startDate} onChange={(e) => e.target.value && set({ endDate: e.target.value })} />
            </label>
          </div>
        ) : (
          <>
            <div className="row event-form-times">
              <label className="field">
                <span>Starts</span>
                <input type="date" className="input" value={d.startDate} onChange={(e) => e.target.value && setStartDate(e.target.value)} />
              </label>
              <label className="field">
                <span>&nbsp;</span>
                <input type="time" className="input" value={d.startTime} onChange={(e) => e.target.value && setStartTime(e.target.value)} />
              </label>
            </div>
            <div className="row event-form-times">
              <label className="field">
                <span>Ends</span>
                <input type="date" className="input" value={d.endDate} min={d.startDate} onChange={(e) => e.target.value && set({ endDate: e.target.value })} />
              </label>
              <label className="field">
                <span>&nbsp;</span>
                <input type="time" className="input" value={d.endTime} onChange={(e) => e.target.value && set({ endTime: e.target.value })} />
              </label>
            </div>
          </>
        )}
        <label className="field">
          <span>Where</span>
          <input className="input" value={d.location} maxLength={300} onChange={(e) => set({ location: e.target.value })} />
        </label>
        <label className="field">
          <span>Notes</span>
          <textarea className="input textarea" rows={3} value={d.description} maxLength={4000} onChange={(e) => set({ description: e.target.value })} />
        </label>
        {!event && members.length > 0 && (
          <div className="field">
            <span>Who’s involved?</span>
            <div className="who-picker">
              {members.map((m) => {
                const on = d.memberIds.includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`who ${on ? "on" : ""}`}
                    style={{ ["--member" as string]: m.color }}
                    onClick={() => set({ memberIds: on ? d.memberIds.filter((x) => x !== m.id) : [...d.memberIds, m.id] })}
                    aria-pressed={on}
                  >
                    <Avatar member={m} size="2.6rem" />
                    <span>{m.displayName}</span>
                  </button>
                );
              })}
            </div>
            <p className="muted small">Colors the event on the wall. It doesn’t send anyone an invitation.</p>
          </div>
        )}
        {event?.recurringEventId && (
          <div className="field">
            <span>This is a repeating event. Change…</span>
            <div className="segmented" role="radiogroup">
              <button type="button" role="radio" aria-checked={scope === "one"} className={scope === "one" ? "on" : ""} onClick={() => setScope("one")}>
                Only this one
              </button>
              <button type="button" role="radio" aria-checked={scope === "series"} className={scope === "series" ? "on" : ""} onClick={() => setScope("series")}>
                Every event in the series
              </button>
            </div>
          </div>
        )}
        {error && (
          <p ref={errorRef} className="banner banner-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Sheet>
  );
}

/** Confirm deleting an event; repeating events ask whether to remove one or all. */
export function DeleteEvent({ event, onDone, onCancel }: { event: CalendarEvent; onDone: () => void; onCancel: () => void }) {
  const { remove } = useEventActions();
  const [error, setError] = useState<string | null>(null);
  const run = (scope: "one" | "series") => remove.mutate({ event, scope }, { onSuccess: onDone, onError: (e) => setError(e.message) });
  if (!event.recurringEventId) {
    return (
      <Confirm
        title="Delete this event?"
        message={error ?? `“${event.displayTitle}” will be removed from Google Calendar for everyone who shares it.`}
        confirmLabel={remove.isPending ? "Deleting…" : "Delete"}
        danger
        onCancel={onCancel}
        onConfirm={() => run("one")}
      />
    );
  }
  return (
    <Sheet
      title="Delete a repeating event"
      onClose={onCancel}
      size="sm"
      footer={
        <>
          <button className="btn" onClick={onCancel} disabled={remove.isPending}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={() => run("series")} disabled={remove.isPending}>
            Every event
          </button>
          <button className="btn btn-danger" onClick={() => run("one")} disabled={remove.isPending}>
            Only this one
          </button>
        </>
      }
    >
      <p className="muted">“{event.displayTitle}” repeats. Remove just this occurrence, or every event in the series, from Google Calendar?</p>
      {remove.isPending && <p className="muted">Deleting…</p>}
      {error && <p className="banner banner-error">{error}</p>}
    </Sheet>
  );
}
